// queries/client.ts

import { Sentry } from "@/hooks/sentry"

import { showErrorToast } from "@/components/toasts/AppToast"
import { QueryCache, QueryClient } from "@tanstack/react-query"

import { type ClientError } from "graphql-request"

const getUserErrorMessage = (error: any): string => {
  if (!error) {
    return "An unknown error occurred. Please try again."
  }

  // HTTP errors
  const statusCode = error.response?.status || 0

  if (statusCode === 400) {
    return "Bad request. Please check the provided data."
  }

  if (statusCode === 403) {
    return "You do not have permission to perform this action."
  }

  if (statusCode === 404) {
    return "Resource not found. Please check the URL or try again later."
  }

  if (statusCode === 408) {
    return "Request timeout. Please check your internet connection and try again."
  }

  if (statusCode === 429) {
    return "Too many requests. Please wait and try again later."
  }

  if (statusCode >= 502) {
    return "Service unavailable. Please try again later."
  }

  if (statusCode >= 500) {
    return "Internal server error. Please try again later."
  }

  // GraphQL-specific errors
  if (error.response?.errors && Array.isArray(error.response.errors)) {
    return error.response.errors.map((err: any) => err.message).join(", ")
  }

  // Handle network or client-side errors (e.g., no response object)
  if (error.message?.includes("Network Error")) {
    return "Network error. Please check your internet connection."
  }

  // Log the error for troubleshooting
  console.error("Unexpected error structure:", error)

  return "Unexpected error. Please try again."
}

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5000,
      retry: (failureCount, error) => {
        const statusCode = (error as ClientError)?.response?.status

        // Retry for certain server or network errors
        if (
          statusCode === 408 ||
          statusCode === 500 ||
          statusCode === 502 ||
          statusCode === 503 ||
          statusCode === 504 ||
          statusCode === 520
        ) {
          console.log(
            `Retrying for status code: ${statusCode}, failureCount: ${failureCount}`
          )
          return failureCount < 3
        }

        // No retry for these errors
        if (
          statusCode === 400 ||
          statusCode === 401 ||
          statusCode === 403 ||
          statusCode === 404 ||
          statusCode === 429
        ) {
          console.log(`Not retrying for status code: ${statusCode}`)
          return false
        }

        // Retry network errors
        if (!statusCode && error.message?.includes("Network Error")) {
          console.log("Retrying network error")
          return failureCount < 3
        }

        // Default: No retry
        return false
      },
    },
  },
  queryCache: new QueryCache({
    onError: (error, query) => {
      console.error("QueryCache error object:", error)

      // Ensure that the error is a ClientError
      const statusCode = (error as ClientError)?.response?.status
      if (statusCode) {
        console.log(`Error detected with status code: ${statusCode}`)
      }

      const message = getUserErrorMessage(error)

      if (statusCode && statusCode >= 500) {
        console.log(`Showing error toast with message: ${message}`)
        showErrorToast({ message })
        Sentry.captureException(error)
      } else {
        console.log("Unexpected error type or no status code:", error)
        showErrorToast({ message })
        Sentry.captureException(error)
      }
    },
  }),
})
